// Importing and exporting places: GPX (Garmin and most GPS apps), KML and KMZ (Google Earth, onX, HuntStand).
import type { Catch, LatLon, Pin, PinType, Route } from "../../shared/model.ts";
import { dbPut } from "../db.ts";
import { $, saveFile } from "../dom.ts";
import { dFmtY, pinTypeLabel, t } from "../i18n.ts";
import { sizeLine, speciesName } from "../species.ts";
import { catches } from "../state.ts";
import { uid } from "../util.ts";
import { setView } from "../views.ts";
import { takeSnapshot } from "../safety.ts";
import { bigMap } from "./bigmap.ts";
import { drawPlaces, pins, placesInfo, routes } from "./places.ts";

interface Places { pins: Pin[]; routes: Route[] }

const xmlEsc = (s: unknown) => String(s ?? "").replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c] ?? c);
const round6 = ([a, b]: LatLon): LatLon => [Math.round(a * 1e6) / 1e6, Math.round(b * 1e6) / 1e6];
const baseName = (fileName: string) => fileName.replace(/\.(gpx|kml|kmz)$/i, "");

// Pin type from a waypoint's symbol, icon or name: Garmin symbols ("Boat Ramp"), onX/HuntStand marker names
// ("Tree Stand", "Trail Camera") and Finnish names ("Passi 3", "Riistakamera") all work.
export function symToType(sym: string, name = ""): PinType {
  const s = (sym + " " + name).toLowerCase();
  if (/stand|blind|ladder|tower|passi|kyttä|kojú|koju|hunting area/.test(s)) return "stand";
  if (/camera|\bcam\b|trail ?cam|kamera/.test(s)) return "camera";
  if (/boat|ramp|marina|anchor|laituri|vene/.test(s)) return "boat";
  if (/fish|kala|hot spot/.test(s)) return "spot";
  if (/park|truck|vehicle|auto/.test(s)) return "parking";
  return "other";
}

export function parseGpx(text: string, fileName: string): Places {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror") || doc.documentElement.localName !== "gpx") throw new Error("not gpx");
  const kids = (n: Element, name: string) => [...n.children].filter((c) => c.localName === name);
  const txt = (n: Element, name: string) => kids(n, name)[0]?.textContent?.trim() ?? "";
  const pt = (n: Element): LatLon | null => {
    const lat = parseFloat(n.getAttribute("lat") ?? ""), lon = parseFloat(n.getAttribute("lon") ?? "");
    return Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null;
  };
  const root = doc.documentElement, out: Places = { pins: [], routes: [] };
  for (const w of kids(root, "wpt")) {
    const p = pt(w);
    if (p) out.pins.push({ id: uid(), type: symToType(txt(w, "sym"), txt(w, "name")), name: txt(w, "name"), notes: txt(w, "desc") || txt(w, "cmt"), lat: p[0], lon: p[1], createdAt: Date.now(), source: "gpx" });
  }
  const addRoute = (name: string, points: LatLon[]) => {
    if (points.length < 2) return;
    // very long tracks are thinned so the map stays fast
    const step = Math.ceil(points.length / 4000);
    const pts = step > 1 ? points.filter((_, i) => i % step === 0 || i === points.length - 1) : points;
    out.routes.push({ id: uid(), name: name || baseName(fileName), points: pts.map(round6), createdAt: Date.now(), source: "gpx" });
  };
  for (const trk of kids(root, "trk")) {
    const pts: LatLon[] = [];
    for (const seg of kids(trk, "trkseg")) for (const p of kids(seg, "trkpt")) { const q = pt(p); if (q) pts.push(q); }
    addRoute(txt(trk, "name"), pts);
  }
  for (const rte of kids(root, "rte")) addRoute(txt(rte, "name"), kids(rte, "rtept").map(pt).filter((p): p is LatLon => p != null));
  return out;
}

// KML: placemarks become pins, lines and areas become routes.
export function parseKml(text: string, fileName: string): Places {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror") || doc.documentElement.localName !== "kml") throw new Error("not kml");
  const all = (n: Element | Document, name: string) => [...n.getElementsByTagNameNS("*", name)];
  const first = (n: Element, name: string): Element | undefined => all(n, name)[0];
  const plain = (s: string | null | undefined) => (s ?? "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim().slice(0, 500);
  const coords = (n: Element | undefined): LatLon[] => (n?.textContent ?? "").trim().split(/\s+/).map((c) => c.split(",").map(Number))
    .filter((c) => Number.isFinite(c[0]) && Number.isFinite(c[1])).map((c) => [c[1], c[0]]);
  const out: Places = { pins: [], routes: [] };
  for (const pm of all(doc, "Placemark")) {
    const name = first(pm, "name")?.textContent?.trim() ?? "", notes = plain(first(pm, "description")?.textContent);
    const hint = (first(pm, "styleUrl")?.textContent ?? "") + " " + (first(pm, "href")?.textContent ?? "");
    for (const point of all(pm, "Point")) {
      const c = coords(first(point, "coordinates"))[0];
      if (c) out.pins.push({ id: uid(), type: symToType(hint, name), name, notes, lat: c[0], lon: c[1], createdAt: Date.now(), source: "kml" });
    }
    for (const line of [...all(pm, "LineString"), ...all(pm, "LinearRing")]) {
      const pts = coords(first(line, "coordinates"));
      if (pts.length >= 2) out.routes.push({ id: uid(), name: name || baseName(fileName), points: pts.map(round6), createdAt: Date.now(), source: "kml" });
    }
  }
  return out;
}

// KMZ is a zip file with a KML inside; read the first .kml entry.
async function kmlFromKmz(file: Blob): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer()), dv = new DataView(buf.buffer);
  let eocd = -1; // end of central directory record
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 66000); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("not a zip");
  let p = dv.getUint32(eocd + 16, true);
  for (let n = dv.getUint16(eocd + 10, true); n > 0; n--) {
    const method = dv.getUint16(p + 10, true), size = dv.getUint32(p + 20, true), nameLen = dv.getUint16(p + 28, true);
    const extra = dv.getUint16(p + 30, true), comment = dv.getUint16(p + 32, true), local = dv.getUint32(p + 42, true);
    const name = new TextDecoder().decode(buf.subarray(p + 46, p + 46 + nameLen));
    if (/\.kml$/i.test(name)) {
      const start = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true), data = buf.subarray(start, start + size);
      if (method === 0) return new TextDecoder().decode(data);
      if (method === 8) return new Response(new Blob([data]).stream().pipeThrough(new DecompressionStream("deflate-raw"))).text();
      throw new Error("unsupported zip");
    }
    p += 46 + nameLen + extra + comment;
  }
  throw new Error("no kml in kmz");
}
async function parsePlacesFile(f: File): Promise<Places> {
  if (/\.kmz$/i.test(f.name)) return parseKml(await kmlFromKmz(f), f.name);
  const text = await f.text();
  if (/\.kml$/i.test(f.name) || /<kml[\s>]/i.test(text.slice(0, 2000))) return parseKml(text, f.name);
  return parseGpx(text, f.name);
}
// Already have it? Same name within about 10 m (so importing the same file twice adds nothing).
const samePin = (a: Pin, b: Pin) => (a.name || "") === (b.name || "") && Math.abs(a.lat - b.lat) < 1e-4 && Math.abs(a.lon - b.lon) < 1e-4;

const GARMIN_SYMBOL: Partial<Record<PinType, string>> = { spot: "Fishing Area", stand: "Hunting Area", boat: "Boat Ramp", parking: "Parking Area" };
export function buildGpx({ pins: ps, routes: rs, catches: cs }: Places & { catches: Catch[] }): string {
  const wpt = (lat: number, lon: number, name: string, desc: string, sym: string) =>
    `  <wpt lat="${lat.toFixed(6)}" lon="${lon.toFixed(6)}">\n    <name>${xmlEsc(name)}</name>${desc ? `\n    <desc>${xmlEsc(desc)}</desc>` : ""}${sym ? `\n    <sym>${sym}</sym>` : ""}\n  </wpt>\n`;
  let s = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="${xmlEsc(t("appName"))}" xmlns="http://www.topografix.com/GPX/1/1">\n`;
  for (const p of ps) s += wpt(p.lat, p.lon, p.name || pinTypeLabel(p.type), [pinTypeLabel(p.type), p.notes].filter(Boolean).join(" – "), GARMIN_SYMBOL[p.type] ?? "Flag, Blue");
  for (const c of cs) if (c.loc) s += wpt(c.loc.lat, c.loc.lon, `${speciesName(c)} ${dFmtY.format(c.time)}`, [sizeLine(c), c.place].filter(Boolean).join(" · "), c.kind === "fish" ? "Fishing Hot Spot Facility" : "Hunting Area");
  for (const r of rs) s += `  <trk>\n    <name>${xmlEsc(r.name || t("route"))}</name>\n    <trkseg>\n${r.points.map(([a, b]) => `      <trkpt lat="${a}" lon="${b}"/>`).join("\n")}\n    </trkseg>\n  </trk>\n`;
  return s + "</gpx>\n";
}
/** Saves pins, routes and catch spots as one GPX file. Returns false when the user cancelled. */
export async function exportGpx(what: Places & { catches: Catch[]; name: string }): Promise<boolean> {
  const safe = (what.name || "places").replace(/[^\w\-åäöÅÄÖ]+/g, "-").slice(0, 40);
  return (await saveFile(new Blob([buildGpx(what)], { type: "application/gpx+xml" }), `${safe}.gpx`, t("gpxTitle"))) !== "cancelled";
}

/* ---------- More → Routes and places ---------- */
function setStatus(msg: string, err = false): void {
  const s = $("#gpx-status");
  s.textContent = msg;
  s.classList.toggle("err", err);
}
$("#gpx-export").addEventListener("click", async () => {
  const withSpots = catches.filter((c) => c.loc);
  if (!pins.length && !routes.length && !withSpots.length) { setStatus(t("gpxNothing"), true); return; }
  try {
    if (await exportGpx({ pins, routes, catches: withSpots, name: `reelhunt-places-${new Date().toISOString().slice(0, 10)}` }))
      setStatus(t("gpxExported", pins.length, routes.length, withSpots.length));
  } catch (e) { console.error(e); setStatus(t("gpxFailed"), true); }
});
$("#gpx-file").addEventListener("change", async (e) => {
  const fileInput = e.currentTarget as HTMLInputElement;
  const files = [...(fileInput.files ?? [])];
  fileInput.value = "";
  let newPins = 0, newRoutes = 0, bad = 0, dup = 0;
  if (files.length) await takeSnapshot("import").catch(() => {}); // a copy first, in case
  for (const f of files) {
    try {
      const got = await parsePlacesFile(f);
      for (const p of got.pins) { if (pins.some((x) => samePin(x, p))) { dup++; continue; } await dbPut("pins", p); pins.push(p); newPins++; }
      for (const r of got.routes) { if (routes.some((x) => x.name === r.name && x.points.length === r.points.length)) { dup++; continue; } await dbPut("routes", r); routes.push(r); newRoutes++; }
    } catch (err) { console.warn(err); bad++; }
  }
  drawPlaces(); placesInfo();
  if (newPins || newRoutes) {
    setStatus(t("gpxImported", newPins, newRoutes) + (dup ? " " + t("gpxDuplicates", dup) : "") + (bad ? " " + t("gpxSomeBad", bad) : ""));
    showImported();
  } else setStatus(dup ? t("gpxAllDuplicates", dup) : t("gpxBad"), !dup);
});
// After an import, open the map on what was added.
function showImported(): void {
  setView("map");
  requestAnimationFrame(() => {
    const pts: LatLon[] = [...pins.map((p): LatLon => [p.lat, p.lon]), ...routes.flatMap((r) => r.points)];
    if (pts.length && bigMap) bigMap.fitBounds(pts, { padding: [40, 40], maxZoom: 15 });
  });
}
